import { Entity } from '@backstage/catalog-model';
import { AccountUser } from '../types/accountUser';
import { Insight } from '../types';

export enum AuthType {
  AWS_ACCESS_KEY = 'AWS_ACCESS_KEY',
  AWS_INSTANCE_PROFILE = 'AWS_INSTANCE_PROFILE',
  AWS_IAM_PROFILE = 'AWS_IAM_PROFILE',
  AWS_IAM_ROLE = 'AWS_IAM_ROLE',
  LOAD_AZURE = 'LOAD_AZURE',
}

export enum SchedulePeriod {
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
}

export enum CloudToolsScope {
  REGION = 'Region',
  ACCOUNT = 'Account',
  RESOURCE = 'Resource',
}

export type Schedule = {
  period: SchedulePeriod;
  hour: number | undefined;
  dayOfWeek: number | undefined;
  dayOfMonth: number | undefined;
};

export type Region = {
  Name: string;
  IsEmpty: boolean;
  OnlyDefaultResources: boolean;
};

export type CloudToolsErrorExport = {
  Errors: CloudToolsError[];
  CombinedErrors: CloudToolsError[];
};

export type CloudToolsError = {
  Message: string;
  Scope: CloudToolsScope;
  ResourceIds: string[];
  ActionName: string;
  ResourceType: string;
};

/**
 * Rule muting known-benign scan errors for an account. Only `actionName` is
 * required; the other fields narrow the rule when set. Muted errors stay
 * recorded in spec.errors for audit but are excluded from surfaces that
 * report scan problems (sync state, warnings, region flags).
 */
export type MutedScanError = {
  actionName: string;
  scope?: CloudToolsScope;
  region?: string;
  resourceType?: string;
};

export type CloudToolsInsightsExport = {
  GlobalExternalAccessAnalyzer?: Insight[];
  RegionalExternalAccessAnalyzer?: Record<string, Insight[]>;
  /** Legacy snake_case shape from older cloudtools exports. */
  global_external_access_analyzer?: Insight[];
  regional_external_access_analyzer?: Record<string, Insight[]>;
};

export interface AccountEntity extends Entity {
  kind: 'Account';
  spec: {
    type: string;
    owner: string;
    domain: string;
    dynamic_uuid: string;
    regions: Region[];
    reportFormatVersion: number;
    inventoryFormatVersion: number;
    catalogFormatVersion: number;
    errors: CloudToolsError[];
    mutedErrors?: MutedScanError[];
    lastScanTime: string | undefined;
    lastSyncTime: string | undefined;
    authType: AuthType | undefined;
    scanSchedule: Schedule | undefined;
    users: AccountUser[];
    /** Stamped by the backend on every admin update; clients echo it back as
     * `expectedUpdatedAt` so stale overwrites are rejected with 409. */
    updatedAt?: string;
  };
}
